import type { MyAccess } from "../access/api";
import { findEntry } from "../routes/switchboard";
import type { SwitchEntry } from "../routes/switchboard";

/** Which Reports page the switchboard serves: the new page ("next") or today's ("legacy"). */
export type ReportsMode = SwitchEntry["mode"];
export const reportsMode = (board?: SwitchEntry[]): ReportsMode => findEntry("/duchot", board)?.entry.mode ?? "legacy";

/**
 * What the person sees on the Reports page (D1.1):
 *  - "all": See visit history, every report in their reach, including reports written by other people;
 *  - "mine": Write reports without that switch, only the reports they wrote ("My reports");
 *  - "none": neither (volunteers, for example): no menu item and a short "not available" page.
 * Every list is limited to the contacts in their reach in every case (the server does that).
 */
export type ReportsScope = "all" | "mine" | "none";

export const reportsScope = (access: Pick<MyAccess, "permissions"> | null | undefined): ReportsScope => {
  const held = access?.permissions ?? [];
  if (held.includes("contacts.see_history")) return "all";
  if (held.includes("reports.write")) return "mine";
  return "none";
};

export const canOpenReports = (access: Pick<MyAccess, "permissions"> | null | undefined): boolean => reportsScope(access) !== "none";

/**
 * The menu rule. While today's page is served it needs See visit history AND today's `read_duchot` (its own guard); once the new
 * page is served it follows the two switches only (ruling S8): See visit history gives "Reports", Write reports alone "My reports".
 */
export const reportsInMenu = (can: (p: "contacts.see_history" | "reports.write") => boolean, hasToday: boolean, mode: ReportsMode = reportsMode()): boolean =>
  mode === "next" ? can("contacts.see_history") || can("reports.write") : can("contacts.see_history") && hasToday;

/** Links into the page (the Dashboard's "Record a visit" and "see all"). */
export const reportsLink = {
  write: () => "/duchot?write=1",
  postponed: (from?: string) => `/duchot?outcome=postponed${from ? `&from=${from}` : ""}`,
};
